/**
 * API client.
 *
 * The session lives in an httpOnly cookie the browser attaches automatically,
 * so no token is ever readable from JavaScript. The CSRF token is a separate,
 * readable cookie echoed back in a header on every state-changing request.
 */

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  const value = match?.[2];
  return value ? decodeURIComponent(value) : null;
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  const method = (rest.method ?? 'GET').toUpperCase();

  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('poaas_csrf');
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    headers,
    credentials: 'include',
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = body?.error ?? {};
    throw new ApiError(
      res.status,
      err.code ?? 'error',
      err.message ?? `Request failed (${res.status})`,
      err.details,
    );
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) => request<T>(path, { method: 'PATCH', json }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  upload: async <T>(path: string, file: File, query = ''): Promise<T> => {
    const form = new FormData();
    form.append('file', file);
    const headers = new Headers();
    const csrf = readCookie('poaas_csrf');
    if (csrf) headers.set('X-CSRF-Token', csrf);

    const res = await fetch(`${BASE}${path}${query}`, {
      method: 'POST',
      body: form,
      headers,
      credentials: 'include',
    });
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = body?.error ?? {};
      throw new ApiError(res.status, err.code ?? 'error', err.message ?? 'Upload failed');
    }
    return body as T;
  },
};

// ── Types (mirror app/schemas/api.py) ───────────────────────────────────────

export type Role =
  | 'ADMIN' | 'MAKER' | 'VERIFIER'
  | 'APPROVER_L1' | 'APPROVER_L2' | 'APPROVER_L3'
  | 'FINANCE_OPS' | 'AUDITOR';

export type OrderState =
  | 'DRAFT' | 'IN_APPROVAL' | 'APPROVED' | 'REJECTED'
  | 'PUBLISHED' | 'EXECUTING' | 'SETTLED' | 'CLOSED' | 'CANCELLED';

export type RiskLevel = 'INFO' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface MoneyDto {
  minor: number;
  currency: string;
  formatted: string;
  major: string;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  department: string | null;
  mfa_enrolled: boolean;
}

export interface RiskFinding {
  id: string;
  agent: string;
  code: string;
  title: string;
  detail: string;
  risk: RiskLevel;
  confidence: number;
  evidence: Record<string, unknown> | null;
  state: string;
}

export interface ApprovalStep {
  id: string;
  level: number;
  role_code: Role;
  state: 'PENDING' | 'APPROVED' | 'REJECTED' | 'INVALIDATED' | 'SKIPPED';
  decided_by_name: string | null;
  decided_at: string | null;
  comment: string | null;
  payload_hash: string | null;
  invalidated_at: string | null;
}

export interface Line {
  id: string;
  line_no: number;
  beneficiary_id: string;
  beneficiary_name: string | null;
  beneficiary_code: string | null;
  department: string | null;
  source_ref: string | null;
  description: string | null;
  amount: MoneyDto;
  payment_mode: string;
  breakup: Record<string, unknown> | null;
  state: 'PENDING' | 'PAID' | 'FAILED' | 'HELD';
  reference_no: string | null;
  settled_at: string | null;
}

export interface OrderSummary {
  id: string;
  voucher_no: string;
  purpose: string;
  txn_type: 'DEBIT' | 'CREDIT';
  value_date: string;
  total: MoneyDto;
  state: OrderState;
  department: string | null;
  bank_account_label: string | null;
  line_count: number;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  version: number;
  approval_progress: {
    total_steps: number;
    approved_steps: number;
    current_level: number | null;
    percent: number;
  } | null;
  risk: { level: RiskLevel; score: number; count: number; top: string | null } | null;
}

export interface OrderDetail extends OrderSummary {
  note: string | null;
  payload_hash: string;
  submitted_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  bank_account_id: string;
  lines: Line[];
  approval_steps: ApprovalStep[];
  risk_findings: RiskFinding[];
  available_transitions: string[];
  can_approve: boolean;
  approval_block_reason: string | null;
}

export interface InboxItem extends OrderSummary {
  step: { id: string; level: number; role_code: Role };
  waiting_since: string;
  requires_reapproval: boolean;
}

export interface Diff {
  approved_at: string | null;
  approved_hash: string | null;
  current_hash: string;
  has_changes: boolean;
  change_count: number;
  total_delta_minor: number;
  total_delta_formatted: string;
  changes: Array<{
    kind: 'field' | 'line_added' | 'line_removed' | 'line_changed';
    field?: string;
    line_no?: number;
    before?: unknown;
    after?: unknown;
    fields?: Record<string, { before: unknown; after: unknown }>;
  }>;
}

export interface Brief {
  summary: string;
  context: string;
  watch_items: string[];
  generated_by: 'llm' | 'rules';
  degraded?: boolean;
  risk: { level: RiskLevel; score: number; count: number; top: string | null };
}

export interface Challenge {
  challenge_id: string;
  step_id: string;
  expires_at: string;
  masked_destination: string;
  payload_hash: string | null;
  dev_code?: string | null;
}

export interface Beneficiary {
  id: string;
  code: string;
  name: string;
  kind: string;
  department: string | null;
  category: string | null;
  account_last4: string | null;
  upi_id: string | null;
  bank_detail_changed_at: string | null;
}

export interface BankAccount {
  id: string;
  label: string;
  account_last4: string;
  ifsc: string | null;
  currency: string;
}

export interface AuditEvent {
  id: number;
  occurred_at: string;
  actor_name: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  summary: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface Dashboard {
  kpis: {
    total_orders: number;
    pending_my_approval: number;
    total_debit: MoneyDto;
    total_credit: MoneyDto;
    open_risk_findings: number;
  };
  by_state: Array<{ state: OrderState; count: number; total: MoneyDto }>;
  pending_my_approval: number;
  recent: OrderSummary[];
  cash_position: Array<{
    account_id: string;
    label: string;
    opening: MoneyDto;
    committed_out: MoneyDto;
    committed_in: MoneyDto;
    projected: MoneyDto;
    is_negative: boolean;
  }>;
  top_risks: RiskFinding[];
  payroll?: {
    active_employees: number;
    latest_run: {
      id: string;
      label: string;
      state: PayRunState;
      total_net_minor: number;
      total_net: MoneyDto;
      employee_count: number;
    } | null;
  };
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// ── Payroll types ────────────────────────────────────────────────────────────

export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERN';
export type TaxRegime = 'OLD' | 'NEW';
export type ComponentType = 'EARNING' | 'DEDUCTION';
export type ComponentCategory =
  | 'BASIC' | 'HRA' | 'DA' | 'SPECIAL' | 'CONVEYANCE' | 'MEDICAL'
  | 'LTA' | 'BONUS' | 'OVERTIME' | 'COMMISSION' | 'CUSTOM_EARNING'
  | 'PF_EMPLOYEE' | 'PF_EMPLOYER' | 'ESI_EMPLOYEE' | 'ESI_EMPLOYER'
  | 'PT' | 'TDS' | 'CUSTOM_DEDUCTION';
export type PayFrequency = 'MONTHLY' | 'BI_MONTHLY' | 'WEEKLY';
export type PayRunState =
  | 'DRAFT' | 'COMPUTING' | 'COMPUTED' | 'IN_APPROVAL'
  | 'APPROVED' | 'PAID' | 'CANCELLED';
export type PayslipState = 'DRAFT' | 'FINALIZED' | 'PAID' | 'CANCELLED';
export type CalcType = 'PERCENT_OF_BASIC' | 'PERCENT_OF_CTC' | 'FIXED';

export interface EmployeeOut {
  id: string;
  employee_code: string;
  beneficiary_id: string;
  beneficiary_name: string | null;
  user_id: string | null;
  designation: string | null;
  department: string | null;
  employment_type: EmploymentType;
  date_of_joining: string;
  date_of_exit: string | null;
  pan_last4: string | null;
  uan: string | null;
  esi_number: string | null;
  tax_regime: TaxRegime;
  pf_opted_out: boolean;
  esi_applicable: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PayComponentOut {
  id: string;
  code: string;
  name: string;
  component_type: ComponentType;
  category: ComponentCategory;
  is_taxable: boolean;
  is_statutory: boolean;
  percentage_of_basic: number | null;
  is_active: boolean;
  sort_order: number;
}

export interface StructureComponentOut {
  id: string;
  component_id: string;
  component_code: string | null;
  component_name: string | null;
  component_type: string | null;
  calc_type: CalcType;
  value: number;
  sort_order: number;
}

export interface SalaryStructureOut {
  id: string;
  name: string;
  description: string | null;
  pay_frequency: PayFrequency;
  is_active: boolean;
  components: StructureComponentOut[];
  created_at: string;
  updated_at: string;
}

export interface EmployeeSalaryOut {
  id: string;
  employee_id: string;
  structure_id: string;
  structure_name: string | null;
  annual_ctc: MoneyDto | null;
  effective_from: string;
  effective_to: string | null;
  note: string | null;
  overrides: Array<{ component_id: string; calc_type: CalcType; value: number }>;
  created_at: string;
}

export interface PayRunSummaryOut {
  id: string;
  label: string;
  period_start: string;
  period_end: string;
  pay_date: string;
  pay_frequency: PayFrequency;
  state: PayRunState;
  employee_count: number;
  total_gross: MoneyDto | null;
  total_deductions: MoneyDto | null;
  total_net: MoneyDto | null;
  total_employer_cost: MoneyDto | null;
  payment_order_id: string | null;
  created_at: string;
  computed_at: string | null;
  approved_at: string | null;
  note: string | null;
}

export interface PayslipOut {
  id: string;
  employee_id: string;
  employee_code: string | null;
  employee_name: string | null;
  state: PayslipState;
  total_working_days: number;
  days_worked: number;
  loss_of_pay_days: number;
  gross: MoneyDto | null;
  total_deductions: MoneyDto | null;
  net_pay: MoneyDto | null;
  employer_cost: MoneyDto | null;
  earnings: Array<Record<string, unknown>> | null;
  deductions: Array<Record<string, unknown>> | null;
  employer_contributions: Array<Record<string, unknown>> | null;
  tax_details: Record<string, unknown> | null;
}

export interface PayRunDetailOut extends PayRunSummaryOut {
  payslips: PayslipOut[];
}

export interface StatutoryConfigOut {
  id: string;
  effective_from: string;
  effective_to: string | null;
  pf_employee_rate_bp: number;
  pf_employer_rate_bp: number;
  pf_wage_ceiling_minor: number;
  esi_employee_rate_bp: number;
  esi_employer_rate_bp: number;
  esi_wage_ceiling_minor: number;
  professional_tax: Record<string, unknown> | null;
  tds_slabs: Record<string, unknown> | null;
  created_at: string;
}
